import { IComponentNode } from 'app/app/editor/index.functions';
import { useAppBuilderContext } from 'app/app/editor/context';
import React, { useEffect, useState } from 'react';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { useFormState } from 'react-hook-form';
import { merge } from 'ts-deepmerge';
import { Box, Button, FormLabel, Stack } from '@mui/material';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import DepsGroup from 'app/app/editor/tabs/settings/deps/DepsGroup';
import { IComponentNodeDeps, IComponentNodeDepsEffect } from 'app/app/editor/tabs/settings/deps';

export default function AttributesEditor({ node }: { node: IComponentNode }) {
    const ctx = useAppBuilderContext();
    const componentName = node.type == 'container' ? 'container' : ctx.page.components[node.params.uid].component;

    const staticForm = useCustomSimplifiedForm({
        component: componentName,
        uid: node.params.uid,
    });

    const [defaultDeps, setDefaultDeps] = useState<Array<IComponentNodeDeps>>(
        getDefaultDeps('visibility', node.params?.attr?.deps ?? []),
    );
    useEffect(() => {
        simpleForm.form.hookFormSetValue('deps', JSON.stringify(defaultDeps), { shouldDirty: true });
    }, [JSON.stringify(defaultDeps)]);

    const attr = node.params?.attr ?? {
        title: '',
        visibility: 'const',
        deps: defaultDeps,
    };
    const simpleForm = useCustomSimplifiedForm({ ...attr, deps: JSON.stringify(attr.deps) });
    const { isDirty } = useFormState({ control: simpleForm.form.hookFormControl });

    const applySettings = () => {
        const formData = simpleForm.form.hookFormGetValues();
        simpleForm.form.hookFormReset(formData);
        node.params.attr = merge(node.params?.attr ?? {}, structuredClone(formData));
        node.params.attr.deps = structuredClone(defaultDeps);
        ctx.setPage(ctx.page, false);
        ctx.sendMessage({ name: 'reloadPage' });
    };

    const cancelClick = () => {
        simpleForm.form.hookFormReset();
        setTimeout(() => setDefaultDeps(JSON.parse(simpleForm.form.hookFormGetValues('deps') ?? '[]')), 10);
    };

    const visibility = simpleForm.form.hookFormWatch('visibility');

    return (
        <Stack sx={{ maxHeight: '100%' }}>
            <Stack spacing={1} sx={{ overflow: 'auto' }}>
                <TemplateFormComponent
                    elementProps={staticForm}
                    component={'FormText'}
                    name={'uid'}
                    props={{ readOnly: true, label: 'Uid' }}
                />
                <TemplateFormComponent
                    elementProps={staticForm}
                    component={'FormText'}
                    name={'component'}
                    props={{ readOnly: true }}
                />
                <TemplateFormComponent elementProps={simpleForm} component={'FormText'} name={'title'} />
                <Stack spacing={1}>
                    <Box sx={{ pb: 1 }}>
                        <FormLabel>Visibility</FormLabel>
                        <TemplateFormComponent
                            elementProps={simpleForm}
                            component={'FormRadioGroup'}
                            componentValues={[
                                { label: 'Always visible', value: 'const' },
                                { label: 'Conditional', value: 'cond' },
                            ]}
                            name={'visibility'}
                        />
                    </Box>
                    {visibility == 'cond' && (
                        <Box>
                            <DepsList deps={defaultDeps} setDeps={setDefaultDeps} effect={'visibility'} />
                        </Box>
                    )}
                </Stack>
            </Stack>
            {isDirty && (
                <Stack spacing={1.5} direction={'row'} sx={{ pt: 1, justifyContent: 'flex-end' }}>
                    <Button onClick={() => applySettings()}>{'Apply'}</Button>
                    <Button variant={'outlined'} color="neutral" onClick={() => cancelClick()}>
                        {'Cancel'}
                    </Button>
                </Stack>
            )}
        </Stack>
    );
}

function getDefaultDeps(effect: IComponentNodeDepsEffect, deps: Array<IComponentNodeDeps>) {
    if (!deps.find((d) => d.effect == effect)) {
        const list = deps.slice();
        list.push({
            effect: effect,
            depsGroup: { condition: 'AND', nodes: [] },
        });
        return list;
    }
    return deps;
}

function DepsList({
    deps,
    setDeps,
    effect,
}: {
    deps: Array<IComponentNodeDeps>;
    setDeps: (deps: Array<IComponentNodeDeps>) => void;
    effect: IComponentNodeDepsEffect;
}) {
    const index = deps.findIndex((d) => d.effect == effect);
    if (index == -1) {
        return null;
    }

    return (
        <DepsGroup
            deps={deps[index].depsGroup}
            onChange={(newDeps) => {
                const list = deps.slice();
                list[index] = { ...list[index], depsGroup: newDeps };
                setDeps(list);
            }}
        />
    );
}
